import { FC } from 'react'

import { useParams } from 'react-router-dom'

import {
  ResourceCreateView,
  ResourceDetailView,
  ResourceListView,
  useResourceListPaging,
} from 'core/resources'
import {
  useInternalEmployeeCreate,
  useInternalEmployeeGetById,
  useInternalEmployeeUpdate,
  useInternalEmployeesGet,
} from 'core/types/generated/employees/hooks'
import { resourceCatalog } from 'core/types/generated/resources'
import {
  employeesRequestSchema,
  employeesResponseSchema,
  employeesUpdateRequestSchema,
} from 'core/utils/generated/formSchemas'

import { asRequestBody, createdId, listRows, record } from './helpers'

const resource = resourceCatalog.employees

type ListQuery = Parameters<typeof useInternalEmployeesGet>[0]['query']

export const EmployeesList: FC = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const paging = useResourceListPaging<ListQuery>('cursor')
  const result = useInternalEmployeesGet({ companyId, query: paging.query })

  return (
    <ResourceListView
      {...listRows(result)}
      paging={paging}
      resource={resource}
      schema={employeesResponseSchema}
    />
  )
}

export const EmployeeCreate: FC = () => {
  const { companyId } = useParams<{ companyId: string }>()
  const { mutateAsync } = useInternalEmployeeCreate({ companyId })

  return (
    <ResourceCreateView
      onCreate={async (body) =>
        createdId(await mutateAsync(asRequestBody(body)))
      }
      resource={resource}
      schema={employeesRequestSchema}
    />
  )
}

export const EmployeeDetail: FC = () => {
  const { companyId, employeeId } = useParams<{
    companyId: string
    employeeId: string
  }>()
  const result = useInternalEmployeeGetById({ companyId, employeeId })
  const update = useInternalEmployeeUpdate({ companyId, employeeId })

  return (
    <ResourceDetailView
      {...record(result)}
      onSave={async (body) => {
        await update.mutateAsync(asRequestBody(body))
      }}
      resource={resource}
      schema={employeesResponseSchema}
      updateSchema={employeesUpdateRequestSchema}
    />
  )
}
